import { Button, ButtonIcon, ButtonText } from '@/components/ui/button'
import { HStack } from '@/components/ui/hstack'
import { Image } from '@/components/ui/image'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import { cn } from '@/lib/cn'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { ArtifactPart, selectConversationMessageById } from '@/redux/slices/aiChatSlice'
import {
  AIGeneratedField,
  AIGeneratedForm,
  selectGeneratedFormById,
  setNavigatedAway,
} from '@/redux/slices/formAIGenerationSlice'
import { createFormThunk } from '@/redux/slices/formSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { Scene } from '@/typings/domain'
import { isFulfilled } from '@reduxjs/toolkit'
import { useRouter } from 'expo-router'
import { ArrowRightIcon, ImageIcon } from 'lucide-react-native'
import { useMemo, useState } from 'react'
import { useWindowDimensions, View } from 'react-native'
import {
  MixedStyleRecord,
  RenderHTMLConfigProvider,
  RenderHTMLSource,
  TRenderEngineProvider,
} from 'react-native-render-html'
import AIGeneratedFormPreview from './AIGeneratedFormPreview'
import { buildFormAttributes, buildFormThemeAttributes } from './buildFormAttributes'

const FORM_DESCRIPTION_BASE_TAG_STYLES: MixedStyleRecord = {
  p: { fontSize: 15, lineHeight: 24, marginVertical: 4 },
}

export function AIGeneratedFormDescriptionRendererWrapper({ children }: React.PropsWithChildren) {
  const [labelColor, linkColor] = usePlatformColor('label', 'link')

  const formDescriptionTagsStyles = useMemo(
    () =>
      ({
        ...FORM_DESCRIPTION_BASE_TAG_STYLES,
        body: { color: labelColor },
        p: { ...FORM_DESCRIPTION_BASE_TAG_STYLES.p, color: labelColor },
        span: { color: labelColor },
        div: { color: labelColor },
        strong: { color: labelColor },
        em: { color: labelColor },
        a: { color: linkColor },
        ul: { color: labelColor },
        ol: { color: labelColor },
        li: { color: labelColor },
      }) satisfies MixedStyleRecord,
    [labelColor, linkColor]
  )

  return (
    <TRenderEngineProvider tagsStyles={formDescriptionTagsStyles}>
      <RenderHTMLConfigProvider>{children}</RenderHTMLConfigProvider>
    </TRenderEngineProvider>
  )
}

type AIGeneratedFormArtifactPartProps = {
  conversationId: string
  part: ArtifactPart
}

export default function AIGeneratedFormArtifactPart({ conversationId, part }: AIGeneratedFormArtifactPartProps) {
  const generatedForm = useAppSelector(state => selectGeneratedFormById(state, part.artifactId))
  const [secondaryLabelColor] = usePlatformColor('secondaryLabel')

  const editedHeaderImageUrls = useAppSelector(state => state.formAIGeneration.headerImageEditResult)

  const { width } = useWindowDimensions()
  const contentWidth = width - 2 * 16 - 2 * 16

  if (!generatedForm) return null

  const { theme, name, description, fields, scene, done } = generatedForm

  const showAsCard = done || generatedForm.updatingState.createdByUpdate

  const resolvedHeaderImageUrl = theme?.headerImageUrl
    ? (editedHeaderImageUrls[theme.headerImageUrl] ?? theme.headerImageUrl)
    : undefined

  return (
    <View className={cn('my-4 overflow-hidden', showAsCard && 'bg-background-50 rounded-xl p-3')}>
      <View
        className={cn(
          'pb-4 bg-background-50 rounded-xl overflow-hidden',
          showAsCard && 'max-h-[240px] bg-background-0'
        )}
      >
        <View className="w-full aspect-[700/210] overflow-hidden">
          {resolvedHeaderImageUrl ? (
            <Image className="w-full h-full" source={{ uri: resolvedHeaderImageUrl }} alt={name} resizeMode="cover" />
          ) : (
            <View className="w-full h-full bg-background-100 flex items-center justify-center">
              <ImageIcon size={40} color={secondaryLabelColor} />
            </View>
          )}
        </View>
        <Text className="px-4 pt-4 pb-2 text-2xl text-typography-900" bold>
          {name}
        </Text>
        {description && (
          <View className="px-4">
            <RenderHTMLSource source={{ html: description ?? '' }} contentWidth={contentWidth} />
          </View>
        )}

        {!showAsCard && (
          <VStack space="md" className="px-4 mt-4">
            {fields?.map((field, index) => (
              <VStack key={field._id} space="xs">
                <Text className="text-lg text-typography-900" bold>
                  {index + 1}. {field.label}
                  {scene === Scene.Exam && typeof field.examSetting?.score === 'number' ? (
                    <Text className="ml-1 text-success-300">（{field.examSetting.score} 分）</Text>
                  ) : null}
                </Text>
                {(scene === Scene.Exam || scene === Scene.Evaluation) &&
                  !!field.choices?.length &&
                  field.choices.map((choice, index) => (
                    <Text key={choice.value || index} className="text-typography-700">
                      {choice.name}
                      {isCorrectAnswer(field, index) && <Text className="ml-1 text-success-300">（正确答案）</Text>}
                      {scene === Scene.Evaluation && typeof choice.score === 'number' && (
                        <Text className="ml-1 text-success-300">（{choice.score} 分）</Text>
                      )}
                    </Text>
                  ))}
                {scene === Scene.Exam && field.examSetting && (
                  <>
                    {field.examSetting.answer?.[0] && (
                      <>
                        <Text className="text-typography-700 mt-1">正确答案：</Text>
                        <Text className="text-typography-700">{field.examSetting.answer[0]}</Text>
                      </>
                    )}
                    {field.examSetting.explanation && (
                      <>
                        <Text className="text-typography-700 mt-1">答案解析：</Text>
                        <Text className="text-typography-700">{field.examSetting.explanation}</Text>
                      </>
                    )}
                  </>
                )}
              </VStack>
            ))}
          </VStack>
        )}
      </View>

      {showAsCard && (
        <GeneratedFormCardActions
          conversationId={conversationId}
          generatedForm={generatedForm}
          editedHeaderImageUrls={editedHeaderImageUrls}
        />
      )}
    </View>
  )
}

type GeneratedFormCardActionsProps = {
  conversationId: string
  generatedForm: AIGeneratedForm
  editedHeaderImageUrls: Record<string, string>
}

function GeneratedFormCardActions({
  conversationId,
  generatedForm,
  editedHeaderImageUrls,
}: GeneratedFormCardActionsProps) {
  const toast = useDefaultToast()
  const router = useRouter()
  const dispatch = useAppDispatch()

  const backendMessageId = useAppSelector(state =>
    generatedForm.messageId
      ? selectConversationMessageById(state, conversationId, generatedForm.messageId)?.backendMessageId
      : undefined
  )

  const [isCreatingFormLoading, setIsCreatingFormLoading] = useState(false)

  const handleCreateForm = async () => {
    if (isCreatingFormLoading) return
    setIsCreatingFormLoading(true)

    const formAttributes = buildFormAttributes(generatedForm, backendMessageId)
    const themeAttributes = generatedForm.theme
      ? await buildFormThemeAttributes(generatedForm.theme, editedHeaderImageUrls)
      : undefined

    const result = await dispatch(createFormThunk({ ...formAttributes, themeAttributes }))
    if (isFulfilled(result)) {
      toast.show('表单创建成功')
      dispatch(setNavigatedAway(true))
      router.replace(`/forms/${result.payload.token}/edit`)
    } else {
      toast.show('表单创建失败')
      console.log(result)
    }

    setIsCreatingFormLoading(false)
  }

  return (
    <HStack className="mt-3" space="md">
      <AIGeneratedFormPreview conversationId={conversationId} generatedForm={generatedForm}>
        {handlePreview => (
          <Button
            size="lg"
            className="flex-1 rounded-xl bg-background-0 data-[active=true]:bg-background-50 data-[active=true]:border-secondary-300 border border-secondary-300"
            onPress={handlePreview}
          >
            <ButtonText className="text-typography-900 data-[active=true]:text-typography-900">预览</ButtonText>
          </Button>
        )}
      </AIGeneratedFormPreview>
      <Button
        size="lg"
        className="rounded-xl"
        style={{ flex: 2 }}
        onPress={handleCreateForm}
        disabled={isCreatingFormLoading}
      >
        <ButtonText>编辑表单</ButtonText>
        <ButtonIcon as={ArrowRightIcon} />
      </Button>
    </HStack>
  )
}

function isCorrectAnswer(field: AIGeneratedField, choiceIndex: number) {
  const choice = field.choices?.[choiceIndex]
  if (!choice || !field.examSetting || !field.examSetting.answer) return false

  return field.examSetting.answer.some(
    answer => parseAnswerToChoiceIndex(answer) === choiceIndex || choice.name === answer
  )
}

const ANSWER_CHAR_CODE_START = 'A'.charCodeAt(0)
const ANSWER_CHAR_CODE_END = 'Z'.charCodeAt(0)

function parseAnswerToChoiceIndex(answer: string) {
  if (!answer) return -1

  const charCode = answer.toUpperCase().charCodeAt(0)
  if (charCode < ANSWER_CHAR_CODE_START || charCode > ANSWER_CHAR_CODE_END) return -1
  return charCode - ANSWER_CHAR_CODE_START
}
